import { useEffect, useState } from 'react';
import { NavLink, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../auth/AuthContext';
import { api } from '../lib/api';
import { initialsFor } from '../lib/format';
import { Icon } from './atoms';
import type { Role } from '@air/shared';

const ROLE_LABEL: Record<Role, string> = {
  employee: 'Employee',
  it_reviewer: 'IT Reviewer',
  legal_reviewer: 'Legal Reviewer',
  admin: 'Admin',
};

const ROLE_SWITCHER_TABS: Array<{ id: Role; label: string }> = [
  { id: 'employee', label: 'Employee' },
  { id: 'it_reviewer', label: 'IT' },
  { id: 'legal_reviewer', label: 'Legal' },
  { id: 'admin', label: 'Admin' },
];

// Map a role to the most useful landing route when admin switches perspective.
const LANDING_ROUTE: Record<Role, string> = {
  employee: '/dashboard',
  it_reviewer: '/reviews/it',
  legal_reviewer: '/reviews/legal',
  admin: '/admin',
};

export function SiteHeader() {
  const { user, realRole, effectiveRole, setViewAs, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const isAdmin = realRole === 'admin';

  // Mirror the registry's ?q in the header so what you typed is visible.
  const urlQ = location.pathname === '/registry' ? (searchParams.get('q') ?? '') : '';
  const [searchValue, setSearchValue] = useState(urlQ);
  useEffect(() => {
    setSearchValue(urlQ);
  }, [urlQ]);

  function runSearch(value: string): void {
    const v = value.trim();
    if (!v) {
      navigate('/registry');
      return;
    }
    navigate(`/registry?q=${encodeURIComponent(v)}`);
  }

  return (
    <header className="site-header">
      <div className="site-header__brand">
        <img src="/design-system/assets/VIN-light.svg" alt="VIN" />
      </div>
      <span className="site-header__divider" />
      <span className="site-header__product">
        <strong>AI Software Registry</strong> &nbsp;·&nbsp; Vendor Approval Workflow
      </span>

      <div className="site-header__search" style={{ maxWidth: 320 }}>
        <Icon name="navigate-arrow" size={13} />
        <input
          placeholder="Search every field — name, vendor, notes, conditions…"
          value={searchValue}
          onChange={(e) => setSearchValue(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') runSearch(searchValue);
            if (e.key === 'Escape') {
              setSearchValue('');
              if (urlQ) navigate('/registry');
            }
          }}
        />
      </div>

      {isAdmin && (
        <div className="site-header__role" title="Admin: preview each role's view">
          {ROLE_SWITCHER_TABS.map((t) => (
            <button
              key={t.id}
              className={effectiveRole === t.id ? 'is-active' : ''}
              onClick={() => {
                setViewAs(t.id);
                navigate(LANDING_ROUTE[t.id]);
              }}
            >
              {t.label}
            </button>
          ))}
        </div>
      )}

      {user && (
        <>
          <div
            className="site-header__user"
            title={`${user.email} · ${ROLE_LABEL[user.role]}`}
          >
            <span className="site-header__avatar">{initialsFor(user.fullName)}</span>
            <span>{user.fullName.split(',')[0]}</span>
          </div>
          <button
            className="btn btn--ghost btn--sm"
            style={{ color: 'var(--color-light-steel)' }}
            onClick={() => void logout()}
          >
            Sign out
          </button>
        </>
      )}
    </header>
  );
}

interface SidebarItem {
  to: string;
  label: string;
  icon: string;
  count?: number;
}

interface SidebarGroup {
  heading: string;
  items: SidebarItem[];
}

export function Sidebar() {
  const { effectiveRole } = useAuth();
  const role: Role = effectiveRole ?? 'employee';

  // Lightweight queue counts for the current role.
  const itCount = useQuery({
    queryKey: ['queue-count', 'it'],
    enabled: role === 'it_reviewer' || role === 'admin',
    queryFn: async () => (await api.itQueue()).length,
    refetchInterval: 30_000,
  });
  const legalCount = useQuery({
    queryKey: ['queue-count', 'legal'],
    enabled: role === 'legal_reviewer' || role === 'admin',
    queryFn: async () => (await api.legalQueue()).length,
    refetchInterval: 30_000,
  });

  const groups = buildGroups(role, itCount.data, legalCount.data);
  const location = useLocation();

  return (
    <aside className="sidebar">
      {groups.map((g) => (
        <div key={g.heading}>
          <div className="sidebar__heading">{g.heading}</div>
          <ul className="sidebar__list">
            {g.items.map((it) => {
              const active =
                location.pathname === it.to ||
                (it.to !== '/' && location.pathname.startsWith(it.to));
              return (
                <li key={it.to} className="sidebar__item">
                  <NavLink to={it.to} className={active ? 'is-active' : ''}>
                    <Icon name={it.icon} size={14} />
                    <span>{it.label}</span>
                    {typeof it.count === 'number' && it.count > 0 && (
                      <span className="sidebar__count">{it.count}</span>
                    )}
                  </NavLink>
                </li>
              );
            })}
          </ul>
        </div>
      ))}

      <div
        style={{
          margin: '18px 12px 8px',
          padding: '14px',
          background: 'var(--color-light-steel)',
          borderRadius: 10,
        }}
      >
        <div
          style={{
            fontSize: 10,
            fontWeight: 800,
            letterSpacing: '.12em',
            textTransform: 'uppercase',
            color: 'var(--color-steel)',
            marginBottom: 6,
          }}
        >
          Need a tool?
        </div>
        <div
          style={{
            fontSize: 12,
            color: 'var(--color-navy)',
            lineHeight: 1.5,
            marginBottom: 10,
          }}
        >
          Check the Approved Registry first — most popular AI tools have already been reviewed.
        </div>
        <NavLink to="/registry" className="btn btn--secondary btn--sm" style={{ width: '100%' }}>
          Browse Registry
        </NavLink>
      </div>
    </aside>
  );
}

function buildGroups(role: Role, itCount?: number, legalCount?: number): SidebarGroup[] {
  if (role === 'employee') {
    return [
      {
        heading: 'My Work',
        items: [
          { to: '/dashboard', label: 'Dashboard', icon: 'navigate-arrow' },
          { to: '/requests/new', label: 'Submit a Request', icon: 'add-plus' },
        ],
      },
      {
        heading: 'Catalog',
        items: [{ to: '/registry', label: 'Approved Registry', icon: 'save-floppydisk' }],
      },
    ];
  }
  if (role === 'it_reviewer') {
    return [
      {
        heading: 'IT Review',
        items: [
          { to: '/reviews/it', label: 'Review Queue', icon: 'edit-penpaper', count: itCount },
        ],
      },
      {
        heading: 'Catalog',
        items: [
          { to: '/registry', label: 'Approved Registry', icon: 'save-floppydisk' },
          { to: '/dashboard', label: 'My Submissions', icon: 'navigate-arrow' },
        ],
      },
    ];
  }
  if (role === 'legal_reviewer') {
    return [
      {
        heading: 'Legal Review',
        items: [
          {
            to: '/reviews/legal',
            label: 'Review Queue',
            icon: 'edit-penpaper',
            count: legalCount,
          },
        ],
      },
      {
        heading: 'Catalog',
        items: [
          { to: '/registry', label: 'Approved Registry', icon: 'save-floppydisk' },
          { to: '/dashboard', label: 'My Submissions', icon: 'navigate-arrow' },
        ],
      },
    ];
  }
  return [
    {
      heading: 'Operations',
      items: [
        { to: '/admin', label: 'Admin Settings', icon: 'pad-lock' },
        { to: '/reviews/it', label: 'IT Queue', icon: 'edit-penpaper', count: itCount },
        { to: '/reviews/legal', label: 'Legal Queue', icon: 'edit-penpaper', count: legalCount },
      ],
    },
    {
      heading: 'Catalog',
      items: [{ to: '/registry', label: 'Approved Registry', icon: 'save-floppydisk' }],
    },
  ];
}
